/**
 * Macro system shared types and catalog.
 *
 * @module macros
 * @see apps/docs/src/content/docs/reference/macros.mdx
 *
 * Macros allow dynamic data generation in workflow test inputs.
 * Format: $MACRO_NAME(args) — resolved at execution time to fresh values.
 */

export interface MacroCatalogEntry {
  /** Macro name (uppercase, e.g. "UUID") */
  name: string;
  /** Human-readable description */
  description: string;
  /** Argument definitions (empty for no-arg macros) */
  args: Array<{
    name: string;
    type: 'number' | 'string';
    description: string;
  }>;
  /** Example resolved output */
  example: string;
}

/**
 * Full catalog of available macros — shared by backend resolver and frontend typeahead.
 */
export const MACRO_CATALOG: MacroCatalogEntry[] = [
  {
    name: 'UUID',
    description: 'Generate a random UUID v4',
    args: [],
    example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  },
  {
    name: 'NOW',
    description: 'Current ISO 8601 timestamp',
    args: [],
    example: '2026-02-19T14:30:00.000Z',
  },
  {
    name: 'TODAY',
    description: 'Current date (YYYY-MM-DD)',
    args: [],
    example: '2026-02-19',
  },
  {
    name: 'DATE',
    description: 'Date offset from today by N days (negative for past, positive for future)',
    args: [{ name: 'days', type: 'number', description: 'Number of days offset (e.g. -7, +30)' }],
    example: '2026-02-26',
  },
  {
    name: 'TIMESTAMP',
    description: 'Current Unix timestamp in milliseconds',
    args: [],
    example: '1771520400000',
  },
  {
    name: 'RANDOM_INT',
    description: 'Random integer in range (inclusive)',
    args: [
      { name: 'min', type: 'number', description: 'Minimum value' },
      { name: 'max', type: 'number', description: 'Maximum value' },
    ],
    example: '42',
  },
  {
    name: 'RANDOM_STRING',
    description: 'Random alphanumeric string',
    args: [{ name: 'length', type: 'number', description: 'Character count' }],
    example: 'xK9mQ',
  },
  {
    name: 'EMAIL',
    description: 'Generate a unique test email address',
    args: [],
    example: 'test-a1b2@example-test.com',
  },
  {
    name: 'FIRST_NAME',
    description: 'Random first name',
    args: [],
    example: 'Jordan',
  },
  {
    name: 'LAST_NAME',
    description: 'Random last name',
    args: [],
    example: 'Chen',
  },
  {
    name: 'PHONE',
    description: 'Random US phone number',
    args: [],
    example: '+1-555-382-7491',
  },
  {
    name: 'SEQUENCE',
    description: 'Auto-incrementing counter (resets per run)',
    args: [{ name: 'name', type: 'string', description: 'Counter name' }],
    example: '1',
  },
  {
    name: 'AI',
    description:
      'AI-generated string for the given prompt (resolved once per run, cached by prompt)',
    args: [
      {
        name: 'prompt',
        type: 'string',
        description: 'Quoted description of what to generate, e.g. "email subject"',
      },
    ],
    example: 'Quick question about your refund',
  },
];

/** Names of all valid macros, derived from MACRO_CATALOG. */
export const VALID_MACRO_NAMES = new Set(MACRO_CATALOG.map((m) => m.name));

export type MacroReferenceIssueCode =
  | 'macro_missing_parentheses'
  | 'macro_unknown'
  | 'macro_invalid_arguments';

export interface MacroReferenceIssue {
  code: MacroReferenceIssueCode;
  /** Full expression when available, otherwise the bare token. */
  token: string;
  /** Macro name without the leading dollar sign. */
  name: string;
  message: string;
  /** Canonical replacement for repairable syntax errors. */
  suggestion?: string;
  index: number;
}

export type WorkflowInputExpressionIssueCode =
  | MacroReferenceIssueCode
  | 'variable_self_reference'
  | 'variable_forward_reference'
  | 'variable_unknown'
  | 'variable_not_value_input'
  | 'variable_invalid_syntax';

export interface WorkflowInputExpressionIssue {
  code: WorkflowInputExpressionIssueCode;
  inputName: string;
  inputIndex: number;
  message: string;
  hint?: string;
  token: string;
}

/** Minimal structural shape accepted by workflow-input expression validation. */
export interface WorkflowInputExpressionDefinition {
  kind: 'value' | 'credential';
  name: string;
  defaultValue?: string;
}

export interface MacroReferenceValidationOptions {
  /** Reserved bare tokens owned by a caller rather than the macro registry. */
  allowedBareTokens?: readonly string[];
}

const MACRO_CANDIDATE_PATTERN = /(?<!\\)\$([A-Z][A-Z0-9_]*)(?:\(([^)]*)\))?/g;

function macroArgs(argsString: string | undefined): string[] {
  return argsString !== undefined && argsString.length > 0 ? argsString.split(',') : [];
}

function integerArgument(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const parsed = Number(value.trim());
  return Number.isInteger(parsed) ? parsed : undefined;
}

function stripArgumentQuotes(value: string | undefined): string {
  return (value ?? '').trim().replace(/^["']|["']$/g, '');
}

function validateMacroArguments(name: string, argsString: string): string | undefined {
  const entry = MACRO_CATALOG.find((macro) => macro.name === name);
  if (!entry) return undefined;

  const args = macroArgs(argsString);
  if (args.length !== entry.args.length) {
    return `expected ${entry.args.length} argument${entry.args.length === 1 ? '' : 's'}, got ${args.length}`;
  }

  if (name === 'DATE') {
    if (integerArgument(args[0]) === undefined) return 'days must be an integer';
  }

  if (name === 'RANDOM_INT') {
    const min = integerArgument(args[0]);
    const max = integerArgument(args[1]);
    if (min === undefined) return 'min must be an integer';
    if (max === undefined) return 'max must be an integer';
    if (min > max) return `min (${min}) must be less than or equal to max (${max})`;
  }

  if (name === 'RANDOM_STRING') {
    const length = integerArgument(args[0]);
    if (length === undefined) return 'length must be an integer';
    if (length < 1 || length > 1000) return `length must be between 1 and 1000, got ${length}`;
  }

  if (name === 'SEQUENCE' && !stripArgumentQuotes(args[0])) return 'name cannot be empty';
  if (name === 'AI' && !stripArgumentQuotes(args[0])) return 'prompt cannot be empty';

  return undefined;
}

/**
 * Validate macro-like tokens in a dynamic string.
 *
 * Built-ins must use their canonical call form (`$FIRST_NAME()`), unknown
 * calls are rejected, arguments mirror the runtime registry constraints, and
 * escaped dollar signs remain literal.
 */
export function validateMacroReferences(
  value: string,
  options?: MacroReferenceValidationOptions
): MacroReferenceIssue[] {
  const issues: MacroReferenceIssue[] = [];
  const allowedBareTokens = new Set(options?.allowedBareTokens ?? []);
  const pattern = new RegExp(MACRO_CANDIDATE_PATTERN.source, MACRO_CANDIDATE_PATTERN.flags);
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(value)) !== null) {
    const [token, name, argsString] = match;
    const hasParentheses = argsString !== undefined;
    const known = VALID_MACRO_NAMES.has(name);

    if (!hasParentheses && allowedBareTokens.has(name)) continue;

    if (!known) {
      issues.push({
        code: 'macro_unknown',
        token,
        name,
        message: `Unknown macro \`${token}\`.`,
        index: match.index,
      });
      continue;
    }

    if (!hasParentheses) {
      const suggestion = `$${name}()`;
      issues.push({
        code: 'macro_missing_parentheses',
        token,
        name,
        message: `Macro \`${token}\` must use parentheses.`,
        suggestion,
        index: match.index,
      });
      continue;
    }

    const argumentError = validateMacroArguments(name, argsString);
    if (argumentError) {
      issues.push({
        code: 'macro_invalid_arguments',
        token,
        name,
        message: `Invalid arguments for \`$${name}()\`: ${argumentError}.`,
        index: match.index,
      });
    }
  }

  return issues;
}

/**
 * Validate input defaults (or an effective scenario value map) in declaration
 * order. An input may compose only earlier value inputs via `$var.<name>`.
 */
export function validateWorkflowInputExpressions(
  definitions: readonly WorkflowInputExpressionDefinition[],
  values?: Readonly<Record<string, string>>
): WorkflowInputExpressionIssue[] {
  const issues: WorkflowInputExpressionIssue[] = [];
  const positions = new Map<string, number>();
  definitions.forEach((definition, index) => {
    if (!positions.has(definition.name)) positions.set(definition.name, index);
  });

  definitions.forEach((definition, inputIndex) => {
    if (definition.kind !== 'value') return;
    const value = values?.[definition.name] ?? definition.defaultValue;
    if (value === undefined) return;

    for (const macroIssue of validateMacroReferences(value)) {
      issues.push({
        code: macroIssue.code,
        inputName: definition.name,
        inputIndex,
        message: macroIssue.message,
        hint: macroIssue.suggestion
          ? `Use the canonical form \`${macroIssue.suggestion}\`.`
          : undefined,
        token: macroIssue.token,
      });
    }

    const malformedVariablePattern = /(?<!\\)\$var(?:\.(?!\w)|\b(?!\.\w))/g;
    let malformedVariableMatch: RegExpExecArray | null;
    while ((malformedVariableMatch = malformedVariablePattern.exec(value)) !== null) {
      issues.push({
        code: 'variable_invalid_syntax',
        inputName: definition.name,
        inputIndex,
        message: `Workflow input "${definition.name}" contains invalid variable reference \`${malformedVariableMatch[0]}\`; use a valid \`$var.<name>\` reference.`,
        token: malformedVariableMatch[0],
      });
    }

    const bareVariablePattern = /(?<!\\)\$([a-z_][A-Za-z0-9_]*)/g;
    let bareVariableMatch: RegExpExecArray | null;
    while ((bareVariableMatch = bareVariablePattern.exec(value)) !== null) {
      const referencedName = bareVariableMatch[1]!;
      if (referencedName === 'var') continue;
      const knownInput = positions.has(referencedName);
      issues.push({
        code: 'variable_invalid_syntax',
        inputName: definition.name,
        inputIndex,
        message: knownInput
          ? `Workflow input "${definition.name}" contains bare reference \`${bareVariableMatch[0]}\`. Use \`$var.${referencedName}\` to reference a preceding input.`
          : `Workflow input "${definition.name}" contains invalid bare reference \`${bareVariableMatch[0]}\`; declare it as a preceding input and use \`$var.${referencedName}\`.`,
        token: bareVariableMatch[0],
      });
    }

    const variablePattern = new RegExp(VARIABLE_PATTERN.source, VARIABLE_PATTERN.flags);
    let variableMatch: RegExpExecArray | null;
    while ((variableMatch = variablePattern.exec(value)) !== null) {
      const referencedName = variableMatch[1]!;
      const token = variableMatch[0];
      const referencedIndex = positions.get(referencedName);

      if (referencedName === definition.name) {
        issues.push({
          code: 'variable_self_reference',
          inputName: definition.name,
          inputIndex,
          message: `Workflow input "${definition.name}" cannot reference itself with \`${token}\`.`,
          token,
        });
      } else if (referencedIndex === undefined) {
        issues.push({
          code: 'variable_unknown',
          inputName: definition.name,
          inputIndex,
          message: `Workflow input "${definition.name}" references unknown input \`${token}\`.`,
          token,
        });
      } else if (referencedIndex > inputIndex) {
        issues.push({
          code: 'variable_forward_reference',
          inputName: definition.name,
          inputIndex,
          message: `Workflow input "${definition.name}" may only reference a preceding value input; "${referencedName}" is declared later.`,
          token,
        });
      } else if (definitions[referencedIndex]?.kind !== 'value') {
        issues.push({
          code: 'variable_not_value_input',
          inputName: definition.name,
          inputIndex,
          message: `Workflow input "${definition.name}" references "${referencedName}", but only preceding value inputs can be referenced.`,
          token,
        });
      }
    }
  });

  return issues;
}

/**
 * Validate all macro expressions in a string. Returns array of invalid macro names.
 * Empty array = all macros valid (or no macros present).
 */
export function findInvalidMacros(value: string): string[] {
  return [
    ...new Set(
      validateMacroReferences(value)
        .filter((issue) => issue.code === 'macro_unknown')
        .map((issue) => issue.name)
    ),
  ];
}

/** Expected arg count per macro, derived from MACRO_CATALOG. */
const MACRO_ARG_COUNTS = new Map(MACRO_CATALOG.map((m) => [m.name, m.args.length]));

/**
 * Normalize macro expressions in a string by stripping extra arguments
 * beyond what the catalog defines. LLMs sometimes add extra hints
 * (e.g. "$RANDOM_STRING(10, alphanumeric)") that crash the resolver.
 */
export function normalizeMacroArgs(value: string): string {
  const pattern = new RegExp(MACRO_PATTERN.source, MACRO_PATTERN.flags);
  return value.replace(pattern, (fullMatch, name: string, argsStr: string) => {
    const expectedArgCount = MACRO_ARG_COUNTS.get(name);
    if (expectedArgCount === undefined) return fullMatch;
    const args = argsStr.length > 0 ? argsStr.split(',') : [];
    if (args.length <= expectedArgCount) return fullMatch;
    const trimmed = args.slice(0, expectedArgCount).join(',');
    return `$${name}(${trimmed})`;
  });
}

/** Regex for matching macro expressions. Negative lookbehind for \ handles escaping. */
export const MACRO_PATTERN = /(?<!\\)\$([A-Z_]+)\(([^)]*)\)/g;

/** Regex for matching $var.{name} variable references (simple, dotted, and step-qualified). */
export const VARIABLE_PATTERN = /(?<!\\)\$var\.(\w+(?:\.\w+)*)/g;

/**
 * Regex for matching step-qualified variable references: `$var.StepN_Label.name`.
 * Group 1 = step qualifier (e.g. "Step2_Create_Client"), Group 2 = variable name.
 */
export const QUALIFIED_VARIABLE_PATTERN = /(?<!\\)\$var\.(\w+)\.(\w+)/g;

// ── Typeahead entry types (used by macro/variable dropdown) ──

export type VariableSource =
  | 'inputValue'
  | 'macro'
  | 'extracted'
  | 'credential'
  | 'seed'
  | 'workflowInput'
  | 'env'
  | 'unknown';

export interface VariableTypeaheadEntry {
  kind: 'variable';
  /** Variable name without $var. prefix (e.g. "project_id") */
  name: string;
  source: VariableSource;
  description: string;
  /** Step-qualified name for disambiguation (e.g. "Step2_Create_Client.client") */
  qualifiedName?: string;
  /** Human-readable step label (e.g. "Step 2: Create Client") */
  stepLabel?: string;
  /** Design-time template or expression (e.g. "Acme Test - $RANDOM_STRING(6)") */
  designTimeValue?: string;
}

export interface MacroTypeaheadEntry extends MacroCatalogEntry {
  kind: 'macro';
}

export type TypeaheadEntry = MacroTypeaheadEntry | VariableTypeaheadEntry;

/** Map macro names to the variable keys they produce at runtime. */
export const MACRO_TO_VAR_KEY: Record<string, string> = {
  UUID: 'uuid',
  EMAIL: 'email',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  PHONE: 'phone',
  NOW: 'now',
  TODAY: 'today',
  DATE: 'date',
  TIMESTAMP: 'timestamp',
  RANDOM_INT: 'random_int',
  RANDOM_STRING: 'random_string',
  SEQUENCE: 'sequence',
  AI: 'ai',
};

/**
 * Scan a string for all `$var.{name}` references, returning the variable names.
 * Also handles step-qualified references like `$var.Step2_Create_Client.name`,
 * extracting just the variable name for backward-compatible tracking.
 */
export function scanStringForVarRefs(text: string): string[] {
  const names: string[] = [];

  // First extract step-qualified references ($var.Step2_Label.name)
  const qualifiedPositions = new Set<number>();
  QUALIFIED_VARIABLE_PATTERN.lastIndex = 0;
  let qMatch: RegExpExecArray | null;
  while ((qMatch = QUALIFIED_VARIABLE_PATTERN.exec(text)) !== null) {
    if (/^Step\d+/.test(qMatch[1]!)) {
      names.push(qMatch[2]!);
      qualifiedPositions.add(qMatch.index);
    }
  }

  // Then extract all references, skipping positions already matched as step-qualified
  VARIABLE_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = VARIABLE_PATTERN.exec(text)) !== null) {
    if (!qualifiedPositions.has(match.index)) {
      names.push(match[1]!);
    }
  }
  return names;
}

/** Scan a string for all `$MACRO()` expressions, returning the macro names. */
export function scanStringForMacros(text: string): string[] {
  const names: string[] = [];
  MACRO_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MACRO_PATTERN.exec(text)) !== null) {
    names.push(match[1]!);
  }
  return names;
}
